import type { Counterparty, PaymentOption } from "@payezy/api-client/public";
import { createPaymentMutation, getPaymentLimitsOptions, listAccountsOptions, listCounterpartiesOptions, listPaymentsQueryKey, quotePaymentMutation } from "@payezy/api-client/public/query";
import { Alert, Badge, Button, Dialog, Field, Input, Select, formatDate, formatUSD, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { handleAuthProblem, problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";
import { railName } from "../lib/payments";

function eta(seconds: number): string {
  if (seconds < 120) return "within minutes";
  if (seconds < 3600 * 6) return `about ${Math.round(seconds / 3600) || 1} hour${seconds >= 5400 ? "s" : ""}`;
  return `${Math.round(seconds / 86400) || 1} business day${seconds >= 129600 ? "s" : ""}`;
}

/** Send money: pick an account and recipient, compare rails (fee, arrival), confirm. */
export function SendMoneyDialog({ recipient, onClose }: { recipient?: Counterparty; onClose: () => void }) {
  const org = useOrg();
  const qc = useQueryClient();
  const path = { orgId: org.org_id };
  const accounts = useQuery(listAccountsOptions({ path }));
  const recipients = useQuery({ ...listCounterpartiesOptions({ path }), enabled: !recipient });
  const limits = useQuery(getPaymentLimitsOptions({ path }));
  const [accountId, setAccountId] = useState("");
  const [cpId, setCpId] = useState(recipient?.id ?? "");
  const [amount, setAmount] = useState("");
  const [asset, setAsset] = useState<"USDC" | "SBC">("USDC");
  const [memo, setMemo] = useState("");
  const [rail, setRail] = useState("");
  const [options, setOptions] = useState<PaymentOption[] | null>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  const list = recipient ? [recipient] : (recipients.data?.items ?? []).filter((c) => c.screening_status === "clear");
  const cp = list.find((c) => c.id === cpId);
  const ext = cp?.accounts[0];
  const account = accounts.data?.items.find((a) => a.id === accountId) ?? accounts.data?.items[0];
  useEffect(() => { setOptions(null); setRail(""); }, [cpId, amount, asset, account?.id]);

  const onError = (e: unknown) => { if (!handleAuthProblem(e)) toast.error(problemMessage(e)); };
  const quote = useMutation({ ...quotePaymentMutation(), onSuccess: (q) => { setOptions(q.options); setRail(q.options[0]?.rail ?? ""); }, onError });
  const create = useMutation({
    ...createPaymentMutation(),
    onSuccess: (p) => {
      toast.success(p.status === "pending_approval" ? "Sent for approval" : "Payment on its way");
      void qc.invalidateQueries({ queryKey: listPaymentsQueryKey({ path }) });
      void qc.invalidateQueries({ queryKey: listAccountsOptions({ path }).queryKey });
      onClose();
    },
    onError,
  });
  const body = () => ({
    account_id: account!.id, external_account_id: ext!.id, amount,
    ...(ext?.type === "wallet" ? { asset } : {}), ...(memo ? { memo } : {}),
  });
  const chosen = options?.find((o) => o.rail === rail);
  const ready = account && ext && Number(amount) > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Send money" description={limits.data ? `Limits: ${formatUSD(limits.data.single)} per payment, ${formatUSD(limits.data.remaining_today)} left today` : undefined} wide>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          if (!options) quote.mutate({ path, body: body() });
          else create.mutate({ path, headers: { "Idempotency-Key": idempotencyKey }, body: { ...body(), rail: rail as never } });
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Field label="From">
            <Select value={account?.id ?? ""} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.data?.items.map((a) => <option key={a.id} value={a.id}>{a.name} · {formatUSD(a.balance.available)} available</option>)}
            </Select>
          </Field>
          <Field label="To">
            <Select value={cpId} onChange={(e) => setCpId(e.target.value)} disabled={!!recipient}>
              {!recipient && <option value="">Choose a recipient</option>}
              {list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Amount (USD)">
            <Input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" />
          </Field>
          {ext?.type === "wallet" ? (
            <Field label="Deliver as">
              <Select value={asset} onChange={(e) => setAsset(e.target.value as "USDC" | "SBC")}>
                <option value="USDC">USDC</option>
                <option value="SBC">SBC</option>
              </Select>
            </Field>
          ) : (
            <Field label="Memo (optional)" hint="Shown to the recipient's bank where supported">
              <Input maxLength={140} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Invoice 1042" />
            </Field>
          )}
        </div>
        {options && (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium text-ink-800">How should it arrive?</legend>
            {options.map((o) => (
              <label key={o.rail} className={`flex cursor-pointer items-center gap-3 rounded-lg p-3 ring-1 ${rail === o.rail ? "ring-2 ring-brand-500" : "ring-ink-200"}`}>
                <input type="radio" name="rail" checked={rail === o.rail} onChange={() => setRail(o.rail)} />
                <span className="flex-1">
                  <span className="block text-sm font-medium">{railName(o.rail)}</span>
                  <span className="block text-xs text-ink-500">Arrives {eta(o.eta_seconds)} (by {formatDate(o.arrives_by, true)})</span>
                </span>
                {!o.brandable && <Badge tone="neutral">Payezy name shown</Badge>}
                <span className="font-mono text-sm">{Number(o.fee) > 0 ? `${formatUSD(o.fee)} fee` : "Free"}</span>
              </label>
            ))}
          </fieldset>
        )}
        {chosen && (
          <Alert tone="info">
            {formatUSD(amount)} to {cp?.name}{Number(chosen.fee) > 0 ? ` plus a ${formatUSD(chosen.fee)} fee` : ""}. The total is reserved from your balance now; payments above your approval threshold wait for an approver.
          </Alert>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          {options ? (
            <Button type="submit" loading={create.isPending} disabled={!chosen}>Send {formatUSD(amount)}</Button>
          ) : (
            <Button type="submit" loading={quote.isPending} disabled={!ready}>Review</Button>
          )}
        </div>
      </form>
    </Dialog>
  );
}
